import { useState } from "react";
import { Plus, Clock, CheckCircle2, Trash2, Target, Flame, Award, Pencil } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { PageHeader, Card, SectionTitle, EmptyState, Field, Tag, ConfirmDialog } from "@/components/lf";
import { useStore, fmtDate, fmtMinutes } from "@/lib/hooks";
import { read, write, update, remove } from "@/lib/storage";
import { CATALOG, getBook } from "@/lib/catalog";
import { startReading, logSession, pct, computeStats } from "@/lib/stats";
import { StorageError } from "@/lib/storage";

const safe = (fn) => { try { fn(); return true; } catch (e) { toast.error(e instanceof StorageError ? e.message : "Terjadi kesalahan."); return false; } };

function AddBookDialog({ open, onClose }) {
  const [mode, setMode] = useState("katalog");
  const [catId, setCatId] = useState(CATALOG[0].id);
  const [f, setF] = useState({ title: "", author: "", totalPages: "" });
  const submit = (e) => {
    e.preventDefault();
    if (mode === "katalog") {
      const c = CATALOG.find((x) => x.id === catId);
      const r = safe(() => startReading({ title: c.title, author: c.author, totalPages: c.pages, catalogId: c.id }));
      if (r) toast.success(`“${c.title}” ditambahkan ke daftar bacaan.`);
    } else {
      if (!f.title.trim()) return toast.error("Judul buku wajib diisi.");
      const ok = safe(() => startReading({ title: f.title.trim(), author: f.author.trim() || "Tanpa penulis", totalPages: Number(f.totalPages) || 0, source: "manual" }));
      if (ok) toast.success("Buku ditambahkan.");
      setF({ title: "", author: "", totalPages: "" });
    }
    onClose();
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="add-book-dialog" className="bg-[#1B1B1F] border-[#29292D] text-[#F2F0EC]">
        <DialogHeader><DialogTitle className="font-serif text-2xl font-medium">Tambah buku</DialogTitle><DialogDescription className="text-[#929197]">Pilih dari katalog atau masukkan buku Anda sendiri.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="flex gap-2">
            {["katalog", "manual"].map((m) => (
              <button type="button" key={m} data-testid={`add-book-mode-${m}`} onClick={() => setMode(m)} className={`flex-1 text-sm py-2 border rounded-sm ${mode === m ? "border-[#E6E2D6] text-[#F2F0EC]" : "border-[#29292D] text-[#929197]"}`}>{m === "katalog" ? "Dari katalog" : "Manual"}</button>
            ))}
          </div>
          {mode === "katalog" ? (
            <Field label="Buku katalog"><select data-testid="add-book-catalog-select" className="lf-field" value={catId} onChange={(e) => setCatId(e.target.value)}>{CATALOG.map((c) => <option key={c.id} value={c.id}>{c.title} — {c.author}</option>)}</select></Field>
          ) : (
            <>
              <Field label="Judul"><input data-testid="add-book-title-input" className="lf-field" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} /></Field>
              <Field label="Penulis"><input data-testid="add-book-author-input" className="lf-field" value={f.author} onChange={(e) => setF({ ...f, author: e.target.value })} /></Field>
              <Field label="Total halaman"><input data-testid="add-book-pages-input" type="number" min="0" className="lf-field" value={f.totalPages} onChange={(e) => setF({ ...f, totalPages: e.target.value })} /></Field>
            </>
          )}
          <button data-testid="add-book-submit" className="lf-btn w-full">Tambahkan</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function SessionDialog({ rec, onClose }) {
  const [f, setF] = useState({ minutes: 30, pages: 10, note: "" });
  if (!rec) return null;
  const submit = (e) => {
    e.preventDefault();
    if (!(Number(f.minutes) > 0)) return toast.error("Durasi harus lebih dari 0 menit.");
    if (safe(() => logSession({ progressId: rec.id, minutes: f.minutes, pagesRead: f.pages, note: f.note }))) toast.success("Sesi membaca tercatat.");
    onClose();
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="session-dialog" className="bg-[#1B1B1F] border-[#29292D] text-[#F2F0EC]">
        <DialogHeader><DialogTitle className="font-serif text-2xl font-medium">Catat sesi membaca</DialogTitle><DialogDescription className="text-[#929197]">{getBook(rec.bookId)?.title}</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Durasi (menit)"><input data-testid="session-minutes-input" type="number" min="1" className="lf-field" value={f.minutes} onChange={(e) => setF({ ...f, minutes: e.target.value })} /></Field>
            <Field label="Halaman dibaca"><input data-testid="session-pages-input" type="number" min="0" className="lf-field" value={f.pages} onChange={(e) => setF({ ...f, pages: e.target.value })} /></Field>
          </div>
          <Field label="Catatan sesi (opsional)"><textarea data-testid="session-note-input" rows={3} className="lf-field" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} /></Field>
          <button data-testid="session-submit" className="lf-btn w-full">Simpan sesi</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EditProgressDialog({ rec, onClose }) {
  const [f, setF] = useState({ currentPage: rec?.currentPage ?? 0, totalPages: rec?.totalPages ?? 0, notes: rec?.notes ?? "" });
  if (!rec) return null;
  const submit = (e) => {
    e.preventDefault();
    const total = Math.max(0, Number(f.totalPages) || 0);
    let cur = Math.max(0, Number(f.currentPage) || 0);
    if (total > 0) cur = Math.min(cur, total);
    if (safe(() => update("reading_progress", rec.id, { currentPage: cur, totalPages: total, notes: f.notes }))) toast.success("Progres diperbarui.");
    onClose();
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="edit-progress-dialog" className="bg-[#1B1B1F] border-[#29292D] text-[#F2F0EC]">
        <DialogHeader><DialogTitle className="font-serif text-2xl font-medium">Perbarui progres</DialogTitle><DialogDescription className="text-[#929197]">{getBook(rec.bookId)?.title}</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Halaman saat ini"><input data-testid="progress-current-input" type="number" min="0" className="lf-field" value={f.currentPage} onChange={(e) => setF({ ...f, currentPage: e.target.value })} /></Field>
            <Field label="Total halaman"><input data-testid="progress-total-input" type="number" min="0" className="lf-field" value={f.totalPages} onChange={(e) => setF({ ...f, totalPages: e.target.value })} /></Field>
          </div>
          <Field label="Catatan"><textarea data-testid="progress-notes-input" rows={3} className="lf-field" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></Field>
          <button data-testid="progress-submit" className="lf-btn w-full">Simpan</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function GoalDialog({ open, onClose, settings }) {
  const [f, setF] = useState({ annualGoalBooks: settings.annualGoalBooks, monthlyGoalMinutes: settings.monthlyGoalMinutes });
  const submit = (e) => {
    e.preventDefault();
    const a = Math.max(1, Number(f.annualGoalBooks) || 1);
    const m = Math.max(10, Number(f.monthlyGoalMinutes) || 10);
    if (safe(() => write("settings", { ...read("settings"), annualGoalBooks: a, monthlyGoalMinutes: m }))) toast.success("Target diperbarui.");
    onClose();
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="goal-dialog" className="bg-[#1B1B1F] border-[#29292D] text-[#F2F0EC]">
        <DialogHeader><DialogTitle className="font-serif text-2xl font-medium">Ubah target membaca</DialogTitle><DialogDescription className="text-[#929197]">Target tahunan dalam jumlah buku, bulanan dalam menit.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Target buku per tahun"><input data-testid="goal-annual-input" type="number" min="1" className="lf-field" value={f.annualGoalBooks} onChange={(e) => setF({ ...f, annualGoalBooks: e.target.value })} /></Field>
          <Field label="Target menit per bulan"><input data-testid="goal-monthly-input" type="number" min="10" className="lf-field" value={f.monthlyGoalMinutes} onChange={(e) => setF({ ...f, monthlyGoalMinutes: e.target.value })} /></Field>
          <button data-testid="goal-submit" className="lf-btn w-full">Simpan target</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function BookRow({ rec, onSession, onEdit, onComplete, onDelete }) {
  const b = getBook(rec.bookId);
  const done = rec.status === "completed";
  return (
    <div className="lf-card p-5" data-testid={`progress-item-${rec.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="font-serif text-2xl text-[#F2F0EC] leading-tight" data-testid="progress-item-title">{b?.title || "Buku dihapus"}</div>
          <div className="text-sm text-[#929197]">{b?.author} · Mulai {fmtDate(rec.startedAt)}{done && ` · Selesai ${fmtDate(rec.completedAt)}`}</div>
        </div>
        <Tag tone={done ? "ivory" : "blue"}>{done ? "Selesai" : "Sedang dibaca"}</Tag>
      </div>
      <div className="mt-4">
        <div className="flex justify-between text-xs text-[#929197] mb-1.5">
          <span>{rec.totalPages > 0 ? `${rec.currentPage} / ${rec.totalPages} halaman` : `${rec.currentPage} halaman · total belum diisi`}</span>
          <span data-testid="progress-item-percent">{pct(rec)}%</span>
        </div>
        <Progress value={pct(rec)} className="h-1.5 bg-[#29292D]" />
      </div>
      {rec.notes && <p className="text-sm text-[#929197] mt-3 italic">“{rec.notes}”</p>}
      <div className="flex flex-wrap gap-2 mt-4">
        {!done && <button data-testid="progress-log-session-button" onClick={onSession} className="lf-btn !py-2"><Clock size={14} />Catat sesi</button>}
        <button data-testid="progress-edit-button" onClick={onEdit} className="lf-btn-ghost !py-2"><Pencil size={14} />Perbarui</button>
        {!done && <button data-testid="progress-complete-button" onClick={onComplete} className="lf-btn-ghost !py-2"><CheckCircle2 size={14} />Tandai selesai</button>}
        <button data-testid="progress-delete-button" onClick={onDelete} className="lf-btn-ghost !py-2 !text-[#d98a8a]"><Trash2 size={14} />Hapus</button>
      </div>
    </div>
  );
}

export default function ProgresMembaca() {
  const progress = useStore("reading_progress");
  const sessions = useStore("reading_sessions");
  const settings = useStore("settings");
  useStore("books");
  const [addOpen, setAddOpen] = useState(false);
  const [goalOpen, setGoalOpen] = useState(false);
  const [sessRec, setSessRec] = useState(null);
  const [editRec, setEditRec] = useState(null);
  const [delRec, setDelRec] = useState(null);
  const st = computeStats(progress, sessions, settings);
  const sortedSessions = [...sessions].sort((a, b) => new Date(b.date) - new Date(a.date));

  const complete = (rec) => {
    safe(() => update("reading_progress", rec.id, { status: "completed", completedAt: new Date().toISOString(), currentPage: rec.totalPages || rec.currentPage }));
    toast.success("Selamat! Buku ditandai selesai.");
  };

  return (
    <div>
      <PageHeader testid="page-title-progres" overline="Ruang kerja" title="Progres Membaca" subtitle="Pantau buku yang sedang dibaca, sesi, target, dan pencapaian Anda, semua dihitung dari data tersimpan.">
        <button data-testid="add-book-button" onClick={() => setAddOpen(true)} className="lf-btn"><Plus size={16} />Tambah buku</button>
      </PageHeader>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
        <Card data-testid="goal-annual-card">
          <div className="flex items-center justify-between"><span className="lf-overline flex items-center gap-2"><Target size={13} />Target tahunan</span><button data-testid="edit-goals-button" onClick={() => setGoalOpen(true)} className="text-xs text-[#929197] hover:text-[#F2F0EC] underline">Ubah</button></div>
          <div className="font-serif text-4xl mt-3">{st.completedThisYear}<span className="text-[#929197] text-2xl"> / {settings.annualGoalBooks} buku</span></div>
          <Progress value={Math.min(100, (st.completedThisYear / settings.annualGoalBooks) * 100)} className="h-1.5 bg-[#29292D] mt-3" />
        </Card>
        <Card data-testid="goal-monthly-card">
          <span className="lf-overline flex items-center gap-2"><Clock size={13} />Target bulan ini</span>
          <div className="font-serif text-4xl mt-3">{fmtMinutes(st.monthMinutes)}<span className="text-[#929197] text-2xl"> / {fmtMinutes(settings.monthlyGoalMinutes)}</span></div>
          <Progress value={Math.min(100, (st.monthMinutes / settings.monthlyGoalMinutes) * 100)} className="h-1.5 bg-[#29292D] mt-3" />
        </Card>
        <Card data-testid="streak-card">
          <span className="lf-overline flex items-center gap-2"><Flame size={13} />Streak membaca</span>
          <div className="font-serif text-4xl mt-3" data-testid="streak-value">{st.streak}<span className="text-[#929197] text-2xl"> hari</span></div>
          <div className="text-xs text-[#929197] mt-3">{st.streak > 0 ? "Pertahankan ritme Anda hari ini." : "Catat sesi hari ini untuk memulai streak."}</div>
        </Card>
      </div>

      <section className="mb-10">
        <SectionTitle>Sedang dibaca</SectionTitle>
        {st.reading.length === 0 ? (
          <EmptyState testid="empty-reading" title="Belum ada buku yang sedang dibaca" text="Tambahkan buku dari katalog atau input manual untuk mulai melacak progres."><button onClick={() => setAddOpen(true)} className="lf-btn">Tambah buku</button></EmptyState>
        ) : (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            {st.reading.map((r) => <BookRow key={r.id} rec={r} onSession={() => setSessRec(r)} onEdit={() => setEditRec(r)} onComplete={() => complete(r)} onDelete={() => setDelRec(r)} />)}
          </div>
        )}
      </section>

      <section className="mb-10">
        <SectionTitle>Aktivitas 14 hari terakhir</SectionTitle>
        <Card>
          {sessions.length === 0 ? <p className="text-sm text-[#929197]" data-testid="empty-chart">Grafik akan muncul setelah Anda mencatat sesi membaca pertama.</p> : (
            <div className="h-56" data-testid="activity-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={st.last14}>
                  <XAxis dataKey="label" tick={{ fill: "#929197", fontSize: 11 }} axisLine={{ stroke: "#29292D" }} tickLine={false} interval={1} />
                  <YAxis tick={{ fill: "#929197", fontSize: 11 }} axisLine={false} tickLine={false} width={30} />
                  <Tooltip cursor={{ fill: "#1B1B1F" }} contentStyle={{ background: "#1B1B1F", border: "1px solid #29292D", borderRadius: 2, color: "#F2F0EC" }} formatter={(v) => [`${v} menit`, "Durasi"]} />
                  <Bar dataKey="minutes" fill="#E6E2D6" radius={[1, 1, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>
      </section>

      <section className="mb-10">
        <SectionTitle>Selesai dibaca</SectionTitle>
        {st.completed.length === 0 ? <EmptyState testid="empty-completed" title="Belum ada buku yang selesai" text="Buku yang Anda tandai selesai akan tampil di sini." /> : (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">{st.completed.map((r) => <BookRow key={r.id} rec={r} onSession={() => {}} onEdit={() => setEditRec(r)} onComplete={() => {}} onDelete={() => setDelRec(r)} />)}</div>
        )}
      </section>

      <section className="mb-10">
        <SectionTitle>Pencapaian</SectionTitle>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="milestones">
          {st.milestones.map((m) => (
            <div key={m.id} data-testid={`milestone-${m.id}`} className={`border p-4 rounded-md ${m.done ? "border-[#E6E2D6]/50 bg-[#151518]" : "border-[#29292D] opacity-60"}`}>
              <Award size={18} className={m.done ? "text-[#E6E2D6]" : "text-[#545B6B]"} />
              <div className="font-serif text-lg mt-2">{m.label}</div>
              <div className="text-xs text-[#929197]">{m.desc}</div>
              <div className="text-[10px] uppercase tracking-widest mt-2 text-[#929197]">{m.done ? "Tercapai" : "Terkunci"}</div>
            </div>
          ))}
        </div>
      </section>

      <section>
        <SectionTitle>Riwayat membaca</SectionTitle>
        {sortedSessions.length === 0 ? <EmptyState testid="empty-history" title="Riwayat masih kosong" text="Setiap sesi yang Anda catat akan tersimpan di sini." /> : (
          <div className="border border-[#29292D] rounded-md divide-y divide-[#29292D]" data-testid="session-history">
            {sortedSessions.slice(0, 20).map((s) => (
              <div key={s.id} className="flex flex-wrap items-center justify-between gap-2 px-5 py-3.5 text-sm bg-[#151518]" data-testid="session-history-item">
                <div><span className="text-[#F2F0EC]">{getBook(s.bookId)?.title || "Buku"}</span>{s.note && <span className="text-[#929197]"> — {s.note}</span>}</div>
                <div className="text-[#929197]">{fmtDate(s.date)} · {s.minutes} menit · {s.pagesRead} hlm</div>
              </div>
            ))}
          </div>
        )}
      </section>

      <AddBookDialog open={addOpen} onClose={() => setAddOpen(false)} />
      {goalOpen && <GoalDialog open onClose={() => setGoalOpen(false)} settings={settings} />}
      {sessRec && <SessionDialog rec={sessRec} onClose={() => setSessRec(null)} />}
      {editRec && <EditProgressDialog rec={editRec} onClose={() => setEditRec(null)} />}
      <ConfirmDialog open={!!delRec} onOpenChange={(o) => !o && setDelRec(null)} title="Hapus catatan progres?" description="Progres buku ini akan dihapus. Riwayat sesi yang sudah tercatat tetap tersimpan." onConfirm={() => { remove("reading_progress", delRec.id); setDelRec(null); toast.success("Progres dihapus."); }} />
    </div>
  );
}
